import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Breadcrumbs,
    Link,
    Switch,
    FormControlLabel,
    InputAdornment,
} from '@mui/material';
import { Save, ArrowBack, AttachMoney, Inventory, Label, NotificationsActive } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { updateVariant, createVariant } from '../../services/Api';

const ModifyVarients = () => {
    const { productId, id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const isEdit = !!id;

    // Context from navigation state
    const productName = location.state?.productName || 'Product';
    const subCategoryName = location.state?.subCategoryName || 'Subcategory';
    const categoryName = location.state?.categoryName || 'Category';
    const categoryId = location.state?.categoryId;
    const subCategoryId = location.state?.subCategoryId;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const [formData, setFormData] = useState({
        variantLabel: '',
        price: '',
        discountPrice: '',
        stockQuantity: '0',
        lowStockAlert: '5',
        isActive: true,
    });

    useEffect(() => {
        if (isEdit && location.state?.variant) {
            const { variant } = location.state;
            setFormData({
                variantLabel: variant.variantLabel || '',
                price: variant.price || '',
                discountPrice: variant.discountPrice || '',
                stockQuantity: variant.stockQuantity !== undefined ? variant.stockQuantity.toString() : '0',
                lowStockAlert: variant.lowStockAlert !== undefined ? variant.lowStockAlert.toString() : '5',
                isActive: variant.isActive !== undefined ? variant.isActive : true,
            });
        }
    }, [isEdit, location.state]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSwitchChange = (e) => {
        setFormData((prev) => ({ ...prev, isActive: e.target.checked }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            let response;
            // The backend expects a 'variants' array even for single variant operations
            const variantEntry = {
                ...formData,
                price: parseFloat(formData.price),
                discountPrice: formData.discountPrice ? parseFloat(formData.discountPrice) : parseFloat(formData.price),
                stockQuantity: parseInt(formData.stockQuantity, 10),
                lowStockAlert: parseInt(formData.lowStockAlert, 10),
            };

            const payload = {
                id: parseInt(productId, 10), // Use 'id' for the product level in this API
                name: productName, // Required by backend even for variant updates
                variants: [variantEntry]
            };

            if (isEdit) {
                // If editing, use the variant ID as the identifier
                response = await updateVariant(id, payload);
            } else {
                // If creating, use the product ID to associate the new variant
                response = await createVariant(payload);
            }

            if (response.success) {
                setSuccess(true);
                setTimeout(() => {
                    navigate(`/sub-categories/${subCategoryId}/products/${productId}/variants`, { 
                        state: { productName, subCategoryName, categoryName, categoryId } 
                    });
                }, 1500);
            } else {
                setError(response.message || 'Failed to save variant');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'An error occurred while saving the variant');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link
                        underline="hover"
                        color="inherit"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate(`/sub-categories/${subCategoryId}/products/${productId}/variants`, { 
                                state: { productName, subCategoryName, categoryName, categoryId } 
                            });
                        }}
                        sx={{ display: 'flex', alignItems: 'center' }}
                    >
                        <ArrowBack sx={{ mr: 0.5, fontSize: 'inherit' }} />
                        {productName} Variants
                    </Link>
                    <Typography color="text.primary">{isEdit ? 'Edit Variant' : 'Create Variant'}</Typography>
                </Breadcrumbs>
                <Typography variant="h4" sx={{ fontWeight: 300 }}>
                    {isEdit ? `Modify Variant: ${formData.variantLabel}` : 'Create New Variant'}
                </Typography>
            </Box>

            <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'text.border' }}>
                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 3 }}>Variant saved successfully! Redirecting...</Alert>}

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                label="Variant Label"
                                name="variantLabel"
                                value={formData.variantLabel}
                                onChange={handleInputChange}
                                required
                                placeholder="e.g. 1 kg, Pack of 6"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Label /></InputAdornment>,
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', ml: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.isActive}
                                            onChange={handleSwitchChange}
                                            color="primary"
                                        />
                                    }
                                    label={formData.isActive ? "Active for customers" : "Hidden from customers"}
                                />
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Regular Price"
                                name="price"
                                value={formData.price}
                                onChange={handleInputChange}
                                required
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><AttachMoney /></InputAdornment>,
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Discount Price"
                                name="discountPrice"
                                value={formData.discountPrice}
                                onChange={handleInputChange}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><AttachMoney /></InputAdornment>,
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Stock Quantity"
                                name="stockQuantity"
                                value={formData.stockQuantity}
                                onChange={handleInputChange}
                                required
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Inventory /></InputAdornment>,
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Low Stock Alert Threshold"
                                name="lowStockAlert"
                                value={formData.lowStockAlert}
                                onChange={handleInputChange}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><NotificationsActive /></InputAdornment>,
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate(`/sub-categories/${subCategoryId}/products/${productId}/variants`, { 
                                        state: { productName, subCategoryName, categoryName, categoryId } 
                                    })}
                                    disabled={loading}
                                    sx={{ px: 4 }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                    disabled={loading}
                                    sx={{ px: 6, backgroundColor: 'primary.main' }}
                                >
                                    {isEdit ? 'Update Variant' : 'Create Variant'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Box>
    );
};

export default ModifyVarients;
